import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  apiErrorMessage,
  TravelBooking,
  useResolveManualBookingId,
  useResolveManualNoRemoteBooking,
  useTravelBookingRefresh,
} from '../queries';

interface Props {
  booking: TravelBooking;
  /** La fila autoritativa que devuelve la resolución. */
  onResolved: (booking: TravelBooking) => void;
  /**
   * Hay una resolución EN VUELO. El modal que contiene el panel bloquea su
   * cierre mientras dure: es una escritura durable e irreversible, y perderla
   * de vista a mitad de camino deja al asesor sin saber cómo terminó.
   */
  onBusyChange?: (busy: boolean) => void;
}

const RUTERO_BOOKING_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * La resolución manual de un hold que quedó sin identidad remota: el asesor ya
 * verificó en el extranet del proveedor y le cuenta al sistema qué encontró.
 *
 * Las dos salidas son las del backend (`docs/RUTERO.md` §5): adjuntar el id de
 * Rutero que verificó, o afirmar que no existe reserva remota — que es
 * irreversible y por eso pide confirmación con el motivo delante.
 *
 * Vive en los DOS modales de reserva (el del flujo y el del listado): el
 * estado es el mismo y la resolución no puede depender de por dónde entró el
 * asesor.
 */
export function ManualReconciliationPanel({
  booking,
  onResolved,
  onBusyChange,
}: Props) {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const resolveManualBookingId = useResolveManualBookingId();
  const resolveManualNoRemote = useResolveManualNoRemoteBooking();
  const refreshBooking = useTravelBookingRefresh();
  const [verifiedRuteroBookingId, setVerifiedRuteroBookingId] = useState('');
  const [confirmingNoRemote, setConfirmingNoRemote] = useState(false);

  const busy = resolveManualBookingId.isLoading || resolveManualNoRemote.isLoading;
  // La limpieza suelta el bloqueo si el panel se desmonta: el candado es del
  // modal, y un panel que ya no está no puede dejarlo cerrado para siempre.
  useEffect(() => {
    onBusyChange?.(busy);
    return () => onBusyChange?.(false);
  }, [busy, onBusyChange]);
  const verifiedIdValid = RUTERO_BOOKING_ID.test(verifiedRuteroBookingId.trim());

  const run = async (action: () => Promise<TravelBooking>, errorKey: string) => {
    try {
      onResolved(await action());
    } catch (error) {
      addToast({ message: apiErrorMessage(error) ?? t(errorKey), color: 'error' });
      await refreshBooking(booking.id, onResolved);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <TextField
        size="small"
        label={t('travelServices.booking.verifiedBookingId')}
        value={verifiedRuteroBookingId}
        onChange={(event) => setVerifiedRuteroBookingId(event.target.value)}
        inputProps={{ maxLength: 36 }}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <LoadingButton
          variant="outlined"
          loading={resolveManualBookingId.isLoading}
          disabled={!verifiedIdValid}
          onClick={() =>
            run(
              () =>
                resolveManualBookingId.mutateAsync({
                  bookingId: booking.id,
                  ruteroBookingId: verifiedRuteroBookingId.trim(),
                }),
              'travelServices.booking.manualAttachError',
            )
          }
        >
          {t('travelServices.booking.attachVerifiedBookingId')}
        </LoadingButton>
        <Button
          color="error"
          disabled={busy}
          onClick={() => setConfirmingNoRemote(true)}
        >
          {t('travelServices.booking.markNoRemoteBooking')}
        </Button>
      </Stack>

      <Dialog
        open={confirmingNoRemote}
        onClose={
          resolveManualNoRemote.isLoading
            ? undefined
            : () => setConfirmingNoRemote(false)
        }
      >
        <DialogTitle>{t('travelServices.booking.confirmNoRemoteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('travelServices.booking.confirmNoRemoteNotice')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={resolveManualNoRemote.isLoading}
            onClick={() => setConfirmingNoRemote(false)}
          >
            {t('travelServices.booking.keepManual')}
          </Button>
          <LoadingButton
            color="error"
            loading={resolveManualNoRemote.isLoading}
            onClick={async () => {
              setConfirmingNoRemote(false);
              await run(
                () => resolveManualNoRemote.mutateAsync(booking.id),
                'travelServices.booking.manualNoRemoteError',
              );
            }}
          >
            {t('travelServices.booking.confirmNoRemote')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
